import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Send, CheckCircle2, X } from "lucide-react";
import { ctaClick } from "@/lib/analytics";

const VERTICALS = ["Gimnasio / CrossFit", "Institución deportiva", "Colegio / Academia", "Clínica / Estética", "Barbería / Salón", "Servicios por suscripción", "Otro"];
const VOLUMES = ["Menos de RD$50,000/mes", "RD$50,000 - RD$250,000/mes", "RD$250,000 - RD$1,000,000/mes", "Más de RD$1,000,000/mes", "No estoy seguro"];

export default function DemoModal({ open, onClose, vertical }) {
  const [form, setForm] = useState({ name: "", business: "", contact: "", vertical: "", volume: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    if (open && vertical) {
      const match = VERTICALS.find((v) => v.toLowerCase().includes(vertical.toLowerCase()));
      if (match) setForm((prev) => ({ ...prev, vertical: match }));
    }
  }, [open, vertical]);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setForm({ name: "", business: "", contact: "", vertical: "", volume: "" });
      setErrors({});
      setSending(false);
      setApiError("");
    }
  }, [open]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "¿Cuál es tu nombre?";
    if (!form.business.trim()) e.business = "¿Cuál es el nombre de tu negocio?";
    if (!form.contact.trim()) e.contact = "Necesitamos un email o WhatsApp";
    if (!form.vertical) e.vertical = "Selecciona tu tipo de negocio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    setApiError("");
    try {
      const apiUrl = process.env.REACT_APP_DEMO_API_URL || "/api/demo";
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, timestamp: new Date().toISOString(), source: "landing-modal" }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Error ${res.status}`);
      }
      setSubmitted(true);
      if (window.posthog) window.posthog.capture("lead_submit", { vertical: form.vertical, source: "landing-modal" });
    } catch (err) {
      setApiError(err.message || "No se pudo enviar. Intenta de nuevo o escríbenos por WhatsApp.");
    }
    setSending(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-[0_40px_80px_-20px_rgba(0,0,0,0.3)] max-h-[85vh] md:max-h-none overflow-y-auto"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#111111]/5 hover:bg-[#111111]/10 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-[#111111]" strokeWidth={2} />
            </button>

            {submitted ? (
              <div className="p-10 md:p-14 text-center">
                <CheckCircle2 className="w-16 h-16 text-[#075a22] mx-auto mb-6" strokeWidth={1.5} />
                <h2 className="font-['Cabinet_Grotesk'] font-black text-3xl md:text-4xl text-[#075a22] mb-4">
                  Recibimos tu solicitud
                </h2>
                <p className="text-lg text-[#111111]/85 leading-relaxed mb-8">
                  Te contactamos en las próximas horas por WhatsApp o email.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href={`https://wa.me/18299278886?text=Hola%2C+quiero+una+demo+de+Kubo+Payments+%28${encodeURIComponent(form.business)}%29`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#075a22] hover:bg-[#05471a] text-white text-base font-bold px-7 py-4 rounded-full transition-all"
                  >
                    Hablar por WhatsApp <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
                  </a>
                  <button
                    onClick={onClose}
                    className="inline-flex items-center justify-center gap-2 bg-[#111111] hover:bg-[#2a2a2a] text-white text-base font-bold px-7 py-4 rounded-full transition-all"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2">
                {/* Left: WhatsApp */}
                <div className="bg-[#111111] p-6 md:p-10 flex flex-col justify-center relative">
                  <div className="absolute -bottom-[1px] right-4 left-4 h-[3px] md:hidden bg-gradient-to-r from-[#a68a6d]/60 via-[#a68a6d] to-[#a68a6d]/60 rounded-full" aria-hidden="true" />
                  <div className="absolute top-4 bottom-4 -right-[1px] w-[3px] hidden md:block bg-gradient-to-b from-[#a68a6d]/60 via-[#a68a6d] to-[#a68a6d]/60 rounded-full" aria-hidden="true" />
                  <div className="mb-5 md:mb-6">
                    <h3 className="font-['Cabinet_Grotesk'] font-black text-xl md:text-3xl text-white leading-tight mb-3 md:mb-4">
                      <span className="sm:hidden">Tus cobros puedes ser automáticos.</span>
                      <span className="hidden sm:block">Tu día de cobro puede ser automático.</span>
                    </h3>
                    <p className="text-sm md:text-base text-white/80 leading-relaxed">
                      Deja de perseguir pagos. Kubo ejecuta tus cobros recurrentes, recupera fallidos y te da visibilidad completa
                    </p>
                  </div>
                  <a
                    href="https://wa.me/18299278886?text=Hola%2C+quiero+una+demo+de+Kubo+Payments"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => ctaClick("modal_whatsapp", "demo_modal")}
                    className="group inline-flex items-center justify-center gap-2 bg-white text-[#075a22] text-sm md:text-base font-bold px-5 py-2.5 md:px-7 md:py-3.5 rounded-full transition-all hover:scale-[1.03] hover:bg-white/90 shadow-[0_8px_20px_rgba(0,0,0,0.15)]"
                  >
                    Escríbenos por WhatsApp
                    <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                  </a>
                  <a
                    href="http://app.kubopayments.com/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center justify-center gap-2 bg-[#8f7659] hover:bg-[#a68a6d] text-white text-sm md:text-base font-bold px-5 py-2.5 md:px-7 md:py-3.5 rounded-full transition-all"
                  >
                    Registrar mi cuenta
                    <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.5} />
                  </a>
                </div>

                {/* Right: Form */}
                <div className="p-6 md:p-10">
                  <h3 className="font-['Cabinet_Grotesk'] font-black text-lg md:text-xl text-[#111111] mb-1">
                    Envíanos un email
                  </h3>
                  <p className="text-xs md:text-sm text-[#111111]/60 mb-4 md:mb-6">
                    Completa el formulario y te responderemos lo antes posible.
                  </p>

                  <form onSubmit={handleSubmit} noValidate className="space-y-3 md:space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#111111] mb-1">Tu nombre</label>
                      <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border bg-white text-sm transition-colors ${errors.name ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
                        placeholder="Ej: Juan Pérez" />
                      {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#111111] mb-1">Nombre del negocio</label>
                      <input type="text" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border bg-white text-sm transition-colors ${errors.business ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
                        placeholder="Ej: CrossFit Santo Domingo" />
                      {errors.business && <p className="mt-1 text-xs text-red-500">{errors.business}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#111111] mb-1">Email o WhatsApp</label>
                      <input type="text" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border bg-white text-sm transition-colors ${errors.contact ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}
                        placeholder="Ej: juan@ejemplo.com o 809-555-5555" />
                      {errors.contact && <p className="mt-1 text-xs text-red-500">{errors.contact}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#111111] mb-1">Tipo de negocio</label>
                      <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border bg-white text-sm transition-colors ${errors.vertical ? "border-red-400" : "border-[#111111]/10 focus:border-[#a68a6d]"}`}>
                        <option value="">Selecciona una opción</option>
                        {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      {errors.vertical && <p className="mt-1 text-xs text-red-500">{errors.vertical}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#111111] mb-1">Volumen mensual estimado</label>
                      <select value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#111111]/10 bg-white text-sm transition-colors focus:border-[#a68a6d]">
                        <option value="">Selecciona un rango</option>
                        {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>

                    {apiError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
                        {apiError}
                      </div>
                    )}

                    <button type="submit" disabled={sending}
                      onClick={() => ctaClick("modal_submit", "demo_modal")}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-base font-bold px-6 py-4 rounded-full transition-all hover:scale-[1.02] shadow-[0_12px_30px_rgba(166,138,109,0.3)] disabled:opacity-50">
                      {sending ? "Enviando..." : "Enviar solicitud"}
                      <Send className="w-4 h-4" strokeWidth={2.5} />
                    </button>

                    <p className="text-center text-xs text-[#111111]/40">
                      Tus datos solo se usan para contactarte. No los vendemos.
                    </p>
                  </form>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
